import { IonAvatar, IonBadge, IonCol, IonGrid, IonIcon, IonImg, IonItem, IonItemOption, IonItemOptions, IonItemSliding, IonLabel, IonRow, useIonRouter } from "@ionic/react";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { BaseMessage as DefinitionBaseMessage } from "@sendbird/chat/lib/__definition";
import { UserMessage } from "@sendbird/chat/message";
import MessageStatus from "@wearesweet/sendbird-uikit-react/ui/MessageStatus";
import format from "date-fns/format";
import isThisYear from "date-fns/isThisYear";
import isToday from "date-fns/isToday";
import isYesterday from "date-fns/isYesterday";
import { t } from "i18next";
import { ellipsisHorizontalCircleOutline, logOutOutline } from "ionicons/icons";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { debug } from "../../../App";
import { AuthenticatedUserContext } from "../../../contexts/AuthenticatedUserContext";
import AuthenticatedUserContextType from "../../../types/AuthenticaterUserContext";
import { OFFICIAL } from "../../../utils/channelStatus";
import { getChannelAvatarSource } from "../../../utils/getChannelAvatarSource";
import { SUPPORTED_MIMES } from "../../../utils/mimes";
import { useViewport } from "../../../utils/useViewport";
import { ChannelTitle } from "./ChannelTitle";
import { LeaveChannelAlert } from "./LeaveChannelAlert";

export const getTypingMessage = (channel: GroupChannel) => {
  const users = channel.getTypingUsers();
  const isOrAre = users.length > 1 ? t("typingPlural") : t("typingSingular");

  return users.map(({ nickname }) => nickname).join(", ") + " " + isOrAre + "...";
};

export const getLastMessage = (channel: GroupChannel, currentMemberId: string) => {
  const message = channel.lastMessage;

  if (!message) {
    return "";
  }

  if (message.isFileMessage()) {
    if (/^voice\//.test(message.type) || message.type.includes("sbu_type=voice")) {
      return t("voiceMessage");
    }

    if (SUPPORTED_MIMES.AUDIO.includes(message.type)) {
      return t("audio");
    }

    if (message.type === "image/gif") {
      return t("gif");
    }

    if (SUPPORTED_MIMES.IMAGE.includes(message.type)) {
      return t("photo");
    }

    return t("file");
  }

  if (message.isMultipleFilesMessage()) {
  }

  //@ts-ignore
  const messageText = message.message;
  let sender = "";

  if (message.isUserMessage() && message.sender.userId !== currentMemberId && channel.memberCount > 2) {
    const userMessage = message as unknown as UserMessage;
    sender = userMessage.sender.nickname;
  } else if (message.isAdminMessage()) {
    //const adminMessage = message as unknown as AdminMessage;
    sender = t("admin");
  }

  if (sender) {
    return (
      <>
        <strong className={"channel-preview__last-message__sender"}>{sender}:</strong>
        <span className={"channel-preview__last-message__message"}>{messageText}</span>
      </>
    );
  }

  return <span className={"channel-preview__last-message__message"}>{messageText}</span>;
};

export const getLastMessageCreatedAt = (message: DefinitionBaseMessage | null) => {
  if (!message) {
    return "";
  }

  const createdAt = message.createdAt;

  if (!createdAt) {
    return "";
  }

  if (isToday(createdAt)) {
    return format(createdAt, "p");
  }
  if (isYesterday(createdAt)) {
    return t("yesterday");
  }
  if (isThisYear(createdAt)) {
    return format(createdAt, "MMM d");
  }
  return format(createdAt, "yyyy/M/d");
};

export const ChannelPreview = (props: any) => {
  const { member } = useContext(AuthenticatedUserContext) as AuthenticatedUserContextType;
  const [leaveChannelAlertIsOpen, setLeaveChannelAlertIsOpen] = useState<boolean>(false);
  const [isOfficial, setIsOfficial] = useState<boolean>(false);
  const [hasLeft, setHasLeft] = useState<boolean>(false);
  const sliding = useRef<HTMLIonItemSlidingElement | null>(null);
  const { isMobile } = useViewport();

  const { isDesktop } = useViewport();
  const router = useIonRouter();

  const channelUrl = useMemo(() => {
    return "/" + (isMobile ? "m" : "") + `chat/channels/${props.channel.url}/highlight/${props.channel.lastMessage?.messageId}`;
  }, [isMobile, props.channel.url, props.channel.lastMessage]);

  const channelSettingsUrl = useMemo(() => {
    return "/" + (isMobile ? "m" : "") + `chat/channels/${props.channel.url}/settings`;
  }, [isMobile, props.channel.url]);

  useEffect(() => {
    setIsOfficial(props.channel.customType.includes(OFFICIAL) || false);
  }, [props.channel.customType]);

  // @ts-ignore
  return hasLeft ? null : (
    <IonItemSliding ref={sliding}>
      <LeaveChannelAlert
        isOpen={leaveChannelAlertIsOpen}
        channel={props.channel}
        onSuccess={() => {
          setHasLeft(true);
          setLeaveChannelAlertIsOpen(false);
        }}
      />

      <IonItem
        routerLink={channelUrl}
        className={
          "channel-preview " +
          (props.isCurrent ? "channel-preview--current " : " ") +
          (props.channel.unreadMessageCount > 0 ? "channel-preview--has-unread-messages " : " ") +
          (isOfficial ? "channel-preview--official " : " ")
        }
      >
        <IonGrid
          fixed
          style={{
            width: "100%",
          }}
          className={"ion-no-padding"}
        >
          <IonRow>
            <IonCol size="2" className={"channel-preview__avatar-container"}>
              <IonAvatar>
                <IonImg src={getChannelAvatarSource(props.channel, member?.id.toString() || "")} />
              </IonAvatar>
            </IonCol>
            <IonCol size="10">
              <IonGrid fixed className={"ion-no-padding"}>
                <IonRow>
                  <IonCol size="9" className={"channel-preview__title-container"}>
                    <ChannelTitle channel={props.channel} currentUserId={member?.id} />
                  </IonCol>
                  <IonCol size="3" className={"channel-preview__last-message__created-at"}>
                    {getLastMessageCreatedAt(props.channel.lastMessage)}
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="10" style={{ display: "inline-flex" }}>
                    {props.channel.lastMessage && props.channel.lastMessage.sender?.userId === member?.id.toString() && <MessageStatus message={props.channel.lastMessage} channel={props.channel} />}
                    <IonLabel className={"ion-no-padding channel-preview__last-message"}>
                      {props.channel.isTyping ? (
                        <IonLabel className={"channel-preview__typing-indicator"}>{getTypingMessage(props.channel)}</IonLabel>
                      ) : (
                        getLastMessage(props.channel, member!.id.toString())
                      )}
                    </IonLabel>
                  </IonCol>
                  <IonCol size="2" className={"channel-preview__unread-container"}>
                    {props.channel.unreadMessageCount > 0 && (
                      <IonBadge color="primary" className={"channel-preview__unread-count"}>
                        {props.channel.unreadMessageCount}
                      </IonBadge>
                    )}
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonItem>

      <IonItemOptions>
        <IonItemOption
          color={"medium"}
          onClick={() => {
            debug("router.push(channelSettingsUrl, isDesktop ? 'none' : 'forward')", channelSettingsUrl, isDesktop ? "none" : "forward");
            router.push(channelSettingsUrl, isDesktop ? "none" : "forward");
            sliding.current?.closeOpened();
          }}
        >
          <IonIcon slot={"icon-only"} icon={ellipsisHorizontalCircleOutline} color={"dark"} />
        </IonItemOption>

        <IonItemOption
          onClick={() => {
            setLeaveChannelAlertIsOpen(true);
          }}
          color={"danger"}
        >
          <IonIcon slot={"icon-only"} icon={logOutOutline} />
        </IonItemOption>
      </IonItemOptions>
    </IonItemSliding>
  );
};
